// Hand-drawn outlines via rough.js. Every sketch is an absolutely positioned
// SVG behind the element's content, drawn in `currentColor` so it themes
// with CSS, and redrawn (with a fixed seed, so it doesn't wobble) on resize.
import rough from 'roughjs';
import type { RoughSVG } from 'roughjs/bin/svg';
import type { Options } from 'roughjs/bin/core';

const NS = 'http://www.w3.org/2000/svg';
let nextSeed = 7;

export const newSeed = (): number => nextSeed++;

export type Draw = (rc: RoughSVG, w: number, h: number) => SVGElement[];

export function sketch(el: Element, draw: Draw, className = ''): { redraw(): void; svg: SVGSVGElement } {
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', `sk ${className}`.trim());
  svg.setAttribute('aria-hidden', 'true');
  el.prepend(svg);
  const rc = rough.svg(svg);
  let lastW = 0;
  let lastH = 0;
  const redraw = (force = false) => {
    const w = Math.round((el as HTMLElement).offsetWidth);
    const h = Math.round((el as HTMLElement).offsetHeight);
    if (!w || !h || (!force && w === lastW && h === lastH)) return;
    lastW = w;
    lastH = h;
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    svg.setAttribute('width', String(w));
    svg.setAttribute('height', String(h));
    svg.replaceChildren(...draw(rc, w, h));
  };
  new ResizeObserver(() => redraw()).observe(el);
  redraw();
  return { redraw: () => redraw(true), svg };
}

export function roundedRect(x: number, y: number, w: number, h: number, r: number): string {
  r = Math.min(r, w / 2, h / 2);
  return `M${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h - r} Q${x + w},${y + h} ${x + w - r},${y + h} H${x + r} Q${x},${y + h} ${x},${y + h - r} V${y + r} Q${x},${y} ${x + r},${y} Z`;
}

export const ink = (o: Options = {}): Options => ({
  stroke: 'currentColor',
  strokeWidth: 1.4,
  roughness: 1.1,
  bowing: 0.8,
  ...o,
});

/** Outline every `.sketched` element. `data-radius` and `data-fill` tune it. */
export function sketchAll(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('.sketched:not([data-sketched])').forEach((el) => {
    el.dataset.sketched = '';
    const seed = newSeed();
    const r = Number(el.dataset.radius ?? (el.classList.contains('rec') ? 999 : 10));
    const round = el.classList.contains('rec');
    sketch(el, (rc, w, h) => {
      const o = ink({ seed, strokeWidth: Number(el.dataset.stroke ?? 1.4) });
      if (round) return [rc.ellipse(w / 2, h / 2, w - 4, h - 4, o)];
      return [rc.path(roundedRect(2, 2, w - 4, h - 4, r), o)];
    });
  });
}
